import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  categoryProductsApi,
  CategoryProductsParams,
} from '../api/categoryProducts';
import { categoriesApi } from '../api/categories';
import ProductList from '../components/home/ProductList';
import Pagination from '../components/main/Pagination';
import SortFilterBar from '../components/main/SortFilterBar';
import BrandFilterSidebar from '../components/shop/BrandFilterSidebar';
import SubcategoryFilterSidebar from '../components/shop/SubcategoryFilterSidebar';
import PageHeader from '../components/main/PageHeader';
import SideMenu from '../components/main/SideMenu';
import type { Product } from '../types/product';
import type { Category } from '../types/category';
import { useBrands } from '../contexts/BrandContext';
import { enhanceProductsWithBrands } from '../utils/brandUtils';

export default function Categories() {
  const { categoryId, categoryName } = useParams<{
    categoryId: string;
    categoryName: string;
  }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { brands } = useBrands();
  const { t } = useTranslation();

  const [rawProducts, setRawProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<
    'new' | 'popular' | 'price_low' | 'price_high'
  >('new');
  const [selectedBrands, setSelectedBrands] = useState<number[]>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<number[]>(
    []
  );
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const limit = 12; // Maximum 12 products per page

  // Enhance products with brand names using cached brand data
  // Filtering is now done server-side via API
  const products = useMemo(() => {
    let enhancedProducts = rawProducts;
    if (rawProducts.length > 0 && brands.length > 0) {
      enhancedProducts = enhanceProductsWithBrands(rawProducts, brands);
    }
    return enhancedProducts;
  }, [rawProducts, brands]);

  const fetchCategoryProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: CategoryProductsParams = {
        page: currentPage,
        limit,
        categoryId: parseInt(categoryId!),
        sortBy,
        // Server-side filtering with brandIds and categoryIds
        brandIds: selectedBrands.length > 0 ? selectedBrands : undefined,
        categoryIds: selectedSubcategories.length > 0 ? selectedSubcategories : undefined,
      };

      const response = await categoryProductsApi.getCategoryProducts(params);
      setRawProducts(response.products || []);
      setTotalPages(response.pagination?.totalPages || response.totalPages || 0);
      setTotalItems(response.pagination?.total || response.total || 0);
    } catch (error) {
      console.error('Error fetching category products:', error);
      // Set fallback values on error
      setRawProducts([]);
      setTotalPages(0);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, categoryId, sortBy, selectedBrands, selectedSubcategories]);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await categoriesApi.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, []);

  const handleSortChange = useCallback(
    (newSortBy: 'new' | 'popular' | 'price_low' | 'price_high') => {
      setSortBy(newSortBy);
      setCurrentPage(1); // Reset to first page when sorting changes
    },
    []
  );

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    if (categoryId) {
      fetchCategoryProducts();
      fetchCategories();
    }
  }, [categoryId, currentPage, limit, fetchCategoryProducts, fetchCategories]);

  // Listen for side menu open event from mobile bottom navigation
  useEffect(() => {
    const handleOpenSideMenu = () => {
      setSideMenuOpen(true);
    };

    window.addEventListener('openSideMenu', handleOpenSideMenu);
    
    return () => {
      window.removeEventListener('openSideMenu', handleOpenSideMenu);
    };
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBrandChange = (brandIds: number[]) => {
    setSelectedBrands(brandIds);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSubcategoryChange = (subcategoryIds: number[]) => {
    setSelectedSubcategories(subcategoryIds);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleMobileFilterToggle = () => {
    setMobileFilterOpen(!mobileFilterOpen);
  };

  const handleClearAllFilters = () => {
    setSelectedBrands([]);
    setSelectedSubcategories([]);
    setCurrentPage(1);
  };

  if (!categoryId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Category not found</Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'white',
        pb: 4,
      }}
    >
      {/* Header */}
      <PageHeader title={decodeURIComponent(categoryName || '')} />

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{ pt: { xs: '60px', md: 2 }, px: { xs: 0, md: '15px' } }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Sidebar - Desktop Only */}
          {!isMobile && (
            <Box
              sx={{
                width: 280,
                flexShrink: 0,
                mt: 2,
              }}
            >
              {/* Clear All Button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  mb: 2,
                }}
              >
                <Typography
                  component="button"
                  onClick={handleClearAllFilters}
                  sx={{
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('category.clearAll')}
                </Typography>
              </Box>
              
              <SubcategoryFilterSidebar
                categories={categories}
                selectedSubcategories={selectedSubcategories}
                selectedBrands={selectedBrands}
                onSubcategoryChange={handleSubcategoryChange}
                currentCategoryId={parseInt(categoryId!)}
                isMobile={false}
              />
              <BrandFilterSidebar
                brands={brands}
                selectedBrands={selectedBrands}
                selectedSubcategories={selectedSubcategories}
                onBrandChange={handleBrandChange}
                isMobile={false}
                categoryId={parseInt(categoryId!)}
              />
            </Box>
          )}

          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
            }}
          >
            {/* Products Count and Mobile Filter Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                ml: 1,
              }}
            >
              {/* Mobile Filter Button */}
              <SortFilterBar
                totalItems={totalItems}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                onFilterClick={isMobile ? handleMobileFilterToggle : undefined}
              />
            </Box>

            {/* Products List */}
            <ProductList
              products={products}
              loading={loading}
              variant="grid"
              fullWidth={true}
            />

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        </Box>
      </Container>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={handleMobileFilterToggle}
        disableScrollLock={false}
        ModalProps={{
          disableScrollLock: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            overflow: 'hidden',
          },
        }}
      >
        <Box 
          sx={{ 
            p: 3,
            height: '100%',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('category.filters')}
            </Typography>
            <Typography
              component="button"
              onClick={handleClearAllFilters}
              sx={{
                color: '#000',
                fontSize: '14px',
                fontWeight: 500,
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
              }}
            >
              {t('category.clearAll')}
            </Typography>
          </Box>

          {/* Combined Filter Panel */}
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 0,
              border: '1px solid #e0e0e0',
              overflow: 'hidden',
            }}
          >
            {/* Subcategories Section */}
            <SubcategoryFilterSidebar
              categories={categories}
              selectedSubcategories={selectedSubcategories}
              selectedBrands={selectedBrands}
              onSubcategoryChange={subcategoryIds => {
                handleSubcategoryChange(subcategoryIds);
              }}
              currentCategoryId={parseInt(categoryId!)}
              isMobile={true}
            />
            
            {/* Divider */}
            <Box sx={{ borderTop: '1px solid #e0e0e0' }} />
            
            {/* Brand Section */}
            <BrandFilterSidebar
              brands={brands}
              selectedBrands={selectedBrands}
              selectedSubcategories={selectedSubcategories}
              onBrandChange={brandIds => {
                handleBrandChange(brandIds);
                setMobileFilterOpen(false);
              }}
              isMobile={true}
              categoryId={parseInt(categoryId!)}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Side Menu */}
      <SideMenu 
        open={sideMenuOpen} 
        onClose={() => setSideMenuOpen(false)} 
      />
    </Box>
  );
}
